/**
 * @file    cprReducer.ts
 * @scope   SHARED
 * @summary CPR slice reducer (plugged into the composed engine reducer, plan §9).
 * @consumes CprSlice
 * @produces CPR_INITIAL_STATE, CprAction, cprReducer
 * @sideEffects None (pure).
 * @notes   MVP: at most `CPR_MAX_ROLLUP_METRICS` (1) rollup per widget, enforced on ADD and RESET.
 *          Programs are de-duplicated by
 *          `dataCollectionGuid` and keep insertion order.
 *          Mounted by `CprProvider` (./CprContext.tsx) through `composeEngineReducer`.
 */
import { CprAggregationType, CprCalculation, CprContributor, CprSlice, CprTrend } from '../model';
import { CPR_MAX_ROLLUP_METRICS } from '../constants';
import { RollupMetricModel } from 'models/CrossProgramRollup/RollupMetricModel';
import { isSameRollupProgram } from 'models/CrossProgramRollup/RollupProgramModel';

export const CPR_INITIAL_STATE: CprSlice = {
  metrics: [],
  aggregationType: undefined,
  trend: undefined,
};

export type CprAction =
  | { type: 'RESET'; state?: CprSlice }
  | { type: 'SET_AGGREGATION_TYPE'; aggregationType?: CprAggregationType }
  | { type: 'SET_TREND'; trend?: CprTrend }
  | { type: 'ADD_ROLLUP_METRIC'; metric: RollupMetricModel }
  | { type: 'REMOVE_ROLLUP_METRIC'; id: string }
  | { type: 'RENAME_ROLLUP_METRIC'; id: string; displayName: string }
  | { type: 'MOVE_ROLLUP_METRIC'; from: number; to: number }
  | { type: 'SET_ROLLUP_CALCULATIONS'; id: string; calculations: CprCalculation[] }
  | { type: 'ADD_ROLLUP_PROGRAM'; id: string; program: CprContributor }
  | { type: 'REMOVE_ROLLUP_PROGRAM'; id: string; dataCollectionGuid: string }
  | { type: 'MOVE_ROLLUP_PROGRAM'; id: string; from: number; to: number }
  | { type: 'SET_ROLLUP_PROGRAMS'; id: string; programs: CprContributor[] }
  | { type: 'UPDATE_ROLLUP_METRIC'; id: string; changes: Partial<Omit<RollupMetricModel, 'id'>> };

/** Applies `update` to the metric with the given id only. */
const updateMetric = (
  state: CprSlice,
  id: string,
  update: (metric: RollupMetricModel) => RollupMetricModel
): CprSlice => ({
  ...state,
  metrics: state.metrics.map((m) => (m.id === id ? update(m) : m)),
});

/** MVP guard: never hold more than `CPR_MAX_ROLLUP_METRICS` rollups, whatever the source. */
const capMetrics = (state: CprSlice): CprSlice =>
  state.metrics.length > CPR_MAX_ROLLUP_METRICS
    ? { ...state, metrics: state.metrics.slice(0, CPR_MAX_ROLLUP_METRICS) }
    : state;

/** Keeps the first occurrence of each program (by `dataCollectionGuid`). */
const dedupePrograms = (programs: CprContributor[]): CprContributor[] =>
  programs.filter((p, i) => programs.findIndex((q) => isSameRollupProgram(p, q)) === i);

const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const cprReducer = (state: CprSlice, action: CprAction): CprSlice => {
  switch (action.type) {
    case 'RESET':
      return capMetrics(action.state ?? CPR_INITIAL_STATE);
    case 'SET_AGGREGATION_TYPE':
      return { ...state, aggregationType: action.aggregationType };
    case 'SET_TREND':
      return { ...state, trend: action.trend };
    case 'ADD_ROLLUP_METRIC':
      // WHY: the backend contract allows a single rollup per widget.
      return state.metrics.length >= CPR_MAX_ROLLUP_METRICS
        ? state
        : { ...state, metrics: [...state.metrics, action.metric] };
    case 'REMOVE_ROLLUP_METRIC':
      return { ...state, metrics: state.metrics.filter((m) => m.id !== action.id) };
    case 'RENAME_ROLLUP_METRIC':
      return updateMetric(state, action.id, (m) => ({ ...m, displayName: action.displayName }));
    case 'MOVE_ROLLUP_METRIC':
      return { ...state, metrics: moveItem(state.metrics, action.from, action.to) };
    case 'SET_ROLLUP_CALCULATIONS':
      return updateMetric(state, action.id, (m) => ({ ...m, calculations: action.calculations }));
    case 'ADD_ROLLUP_PROGRAM':
      return updateMetric(state, action.id, (m) =>
        m.programs.some((p) => isSameRollupProgram(p, action.program))
          ? m
          : { ...m, programs: [...m.programs, action.program] }
      );
    case 'REMOVE_ROLLUP_PROGRAM':
      return updateMetric(state, action.id, (m) => ({
        ...m,
        programs: m.programs.filter((p) => p.dataCollectionGuid !== action.dataCollectionGuid),
      }));
    case 'MOVE_ROLLUP_PROGRAM':
      return updateMetric(state, action.id, (m) => ({
        ...m,
        programs: moveItem(m.programs, action.from, action.to),
      }));
    case 'SET_ROLLUP_PROGRAMS':
      return updateMetric(state, action.id, (m) => ({
        ...m,
        programs: dedupePrograms(action.programs),
      }));
    case 'UPDATE_ROLLUP_METRIC':
      return updateMetric(state, action.id, (m) => ({ ...m, ...action.changes }));
    default:
      return state;
  }
};
